import { AmplifyClassV6 } from '@aws-amplify/core'
import { amplifyConfig } from './aws-exports'
import { GraphQLAPI, GraphQLResult } from '@aws-amplify/api-graphql'
import { Observable, Subscription } from 'rxjs'
import { ISubscriptionParams } from './graphql/interfaces/subscription'
import { Hub } from 'aws-amplify/utils'
import { ConnectionState, CONNECTION_STATE_CHANGE } from '@aws-amplify/api'
import { action, observable } from 'mobx'
import { IAmplifyConnectionState } from './amplify/interfaces'
import { LocalStorage } from '../utilities/LocalStorage'
import { IQueryParams } from './graphql/interfaces/query'

/**
 * Resolves an Authorization header for the AppSync lambda authorizer.
 *
 * This is deliberately a function rather than a captured string. Amplify re-resolves
 * `additionalHeaders` on every connection attempt — including its own internal reconnects — so a
 * token refreshed after page load is picked up without a reload. Passing a static `authToken`
 * option instead would be captured once in Amplify's subscribe() closure AND would override
 * anything this function returns, which is what left long-lived POS tabs presenting an expired
 * token until they were manually refreshed.
 */
type AuthHeaderResolver = () => Promise<{ Authorization: string }>

export class AppSyncAmplify {
  /**
   * AppSync rejects a subscription with a per-subscription `error` frame and no connection-state
   * change, so Amplify's own reconnect machinery is never armed for it. Recovery has to be owned
   * here, and it has to be bounded: every device loses its subscription at the same moment when
   * the platform API is deployed.
   */
  static readonly MAX_RESUBSCRIBE_ATTEMPTS = 5
  /**
   * How long a resubscribe must survive before we treat it as connected and backfill. AppSync
   * rejects an unauthorized `start` frame in about a second, so anything still alive after this
   * has been accepted. There is no client-visible start_ack to key off instead.
   */
  static readonly RESUBSCRIBE_SETTLE_MS = 5000
  private static readonly RESUBSCRIBE_BASE_DELAY_MS = 1000
  private static readonly RESUBSCRIBE_MAX_DELAY_MS = 30000

  readonly amplify: AmplifyClassV6

  @observable
  connectionState: IAmplifyConnectionState = {
    previous: ConnectionState.Disconnected,
    current: ConnectionState.Disconnected,
  }

  constructor() {
    /**
     * Amplify.configure(amplifyConfig) does not work for some reason.
     * error: "Subscribe only available for AWS AppSync endpoint."
     * For some reason, internal "resolveConfig" function does not see "config.API.GraphQL" attribute even though
     * the value exists.
     *
     * please instantiate AmplifyClassV6 directly instead
     */
    this.amplify = new AmplifyClassV6()
    this.amplify.configure(amplifyConfig)
    this.addApiListener()
  }

  createSubscription<T>(params: ISubscriptionParams<T>): Subscription {
    let attempt = 0
    let stopped = false
    let currentSubscription: Subscription | undefined
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined
    let settleTimeoutId: ReturnType<typeof setTimeout> | undefined

    const connect = (isResubscribe: boolean) => {
      const observable = GraphQLAPI.graphql(
        this.amplify,
        {
          query: params.query,
          variables: params.variables,
          authMode: 'lambda',
        },
        this.subscriptionAuthHeaders,
      ) as Observable<GraphQLResult<T>>

      currentSubscription = observable.subscribe({
        next: (value) => {
          // Data proves the subscription is healthy again, so restore the retry budget.
          attempt = 0
          params.callbacks?.next?.(value)
        },
        error: (error) => {
          // This attempt failed, so it never became a resubscribe worth backfilling for.
          if (settleTimeoutId) clearTimeout(settleTimeoutId)
          params.callbacks?.error?.(error)
          scheduleResubscribe()
        },
        complete: params.callbacks?.complete,
      })

      if (isResubscribe && params.onResubscribed) {
        /**
         * Events emitted while we were disconnected are only recoverable over REST. Wait for the
         * subscription to settle first: backfilling on every failed attempt would send the whole
         * estate at the API right when it is already refusing connections.
         */
        settleTimeoutId = setTimeout(params.onResubscribed, AppSyncAmplify.RESUBSCRIBE_SETTLE_MS)
      }
    }

    const scheduleResubscribe = () => {
      if (stopped || attempt >= AppSyncAmplify.MAX_RESUBSCRIBE_ATTEMPTS) return

      attempt += 1
      retryTimeoutId = setTimeout(() => connect(true), this.resubscribeDelay(attempt))
    }

    /**
     * Only an explicit unsubscribe tears the caller down. Registering `teardown` on the inner
     * subscription instead would also run it on `error`, disposing the very recovery the caller
     * relies on.
     */
    const handle = new Subscription(() => {
      stopped = true
      if (retryTimeoutId) clearTimeout(retryTimeoutId)
      if (settleTimeoutId) clearTimeout(settleTimeoutId)
      currentSubscription?.unsubscribe()
      params.teardown()
    })

    connect(false)

    return handle
  }

  async performQuery(params: IQueryParams): Promise<unknown> {
    const result = await GraphQLAPI.graphql(
      this.amplify,
      {
        query: params.query,
        variables: params.variables,
      },
      this.queryAuthHeaders,
    )
    return result
  }

  /**
   * Subscriptions are user-first; queries are device-first. The split is intentional — see
   * commit a6823e21 (MV-1999), which made POS queries prefer the device token.
   */
  private subscriptionAuthHeaders: AuthHeaderResolver = async () => ({
    Authorization: (await this.readAuthToken()) || (await this.readDeviceAuthToken()),
  })

  private queryAuthHeaders: AuthHeaderResolver = async () => ({
    Authorization: (await this.readDeviceAuthToken()) || (await this.readAuthToken()),
  })

  private async readAuthToken() {
    return (await LocalStorage.getAuthToken()) || ''
  }

  private async readDeviceAuthToken() {
    return (await LocalStorage.getDeviceAuthToken()) || ''
  }

  /** Exponential backoff with jitter, so a fleet-wide failure does not resubscribe in lockstep. */
  private resubscribeDelay(attempt: number) {
    const cappedDelay = Math.min(
      AppSyncAmplify.RESUBSCRIBE_BASE_DELAY_MS * 2 ** (attempt - 1),
      AppSyncAmplify.RESUBSCRIBE_MAX_DELAY_MS,
    )
    return cappedDelay * (0.5 + Math.random() * 0.5)
  }

  // https://docs.amplify.aws/javascript/build-a-backend/graphqlapi/subscribe-data/
  private addApiListener() {
    Hub.listen('api', (data: any) => {
      const { payload } = data
      if (payload.event === CONNECTION_STATE_CHANGE) {
        this.setConnectionState({
          previous: this.connectionState.current,
          current: payload.data.connectionState,
        })
      }
    })
  }

  @action
  setConnectionState(connectionState: IAmplifyConnectionState) {
    this.connectionState = connectionState
  }
}
